<template>
    <div class="ProcessDisplayDrawerPanel">
        <q-btn
            color="primary"
            dense
            icon="schema"
            noCaps
            outline
            :disable="disableElements || !canSaveBpmn"
            :label="$t('visualization.bpmnSave')"
            class="full-width q-mb-md"
            @click="handleSaveBpmn"
        />
        <q-expansion-item
            defaultOpened
            dense
            denseToggle
            icon="tune"
            class="ContextDrawerExpansion"
            headerClass="ContextDrawerExpansionHeader"
            :label="$t('visualization.options')"
        >
            <div class="ContextDrawerExpansionContent">
                <q-list dense>
                    <q-item>
                        <q-item-section>
                            <q-select
                                v-model="percentageModeModel"
                                :label="$t('visualization.changePercentage')"
                                behavior="menu"
                                dense
                                emitValue
                                mapOptions
                                outlined
                                :disable="disableElements"
                                :options="percentageOptions"
                                style="min-width: 160px;"
                            />
                        </q-item-section>
                    </q-item>

                    <q-item>
                        <q-item-section>
                            <q-select
                                v-model="directionValueModel"
                                :label="$t('visualization.changeGraphDirection')"
                                behavior="menu"
                                dense
                                emitValue
                                mapOptions
                                outlined
                                :disable="disableElements"
                                :options="directionOptions"
                                style="min-width: 160px;"
                            />
                        </q-item-section>
                    </q-item>

                    <q-item>
                        <q-item-section>
                            <q-select
                                v-model="statsValueModel"
                                :label="$t('visualization.changeGraphMetric')"
                                behavior="menu"
                                dense
                                emitValue
                                mapOptions
                                outlined
                                :disable="disableElements"
                                :options="statsOptions"
                                style="min-width: 160px;"
                            />
                        </q-item-section>
                    </q-item>

                    <q-item v-if="showHeatmapOption">
                        <q-item-section>
                            <q-select
                                v-model="heatmapModeModel"
                                label="Heat map"
                                behavior="menu"
                                dense
                                emitValue
                                mapOptions
                                outlined
                                :disable="disableElements"
                                :options="heatmapOptions"
                                style="min-width: 160px;"
                            />

                            <div v-if="heatmapModeModel !== 'OFF'" class="HeatmapLegend q-mt-sm">
                                <span class="HeatmapLegendLabel">Low</span>
                                <div class="HeatmapLegendBar" />
                                <span class="HeatmapLegendLabel">High</span>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item v-if="frequencyIndicatorState.visible">
                        <q-item-section>
                            <span>{{ $t('visualization.frequencyIndicator.percentOfActivities') }}</span>
                            <div class="row items-center q-gutter-sm">
                                <q-btn
                                    color="primary"
                                    dense
                                    icon="remove"
                                    noCaps
                                    outline
                                    :disable="disableElements || !frequencyIndicatorState.canDecrease"
                                    @click="handleDecreaseFrequencyIndicator"
                                />
                                <span>{{ frequencyIndicatorState.value }}%</span>
                                <q-btn
                                    color="primary"
                                    dense
                                    icon="add"
                                    noCaps
                                    outline
                                    :disable="disableElements || !frequencyIndicatorState.canIncrease"
                                    @click="handleIncreaseFrequencyIndicator"
                                />
                            </div>
                        </q-item-section>
                    </q-item>
                </q-list>
            </div>
        </q-expansion-item>
    </div>
</template>

<script>
import VueTypes from 'vue-types'

export default {
    name: 'ProcessDisplayDrawerPanel',
    props: {
        percentageMode: VueTypes.string.def('TOTAL'),
        directionValue: VueTypes.string.def('LR'),
        statsValue: VueTypes.string.def('averages'),
        heatmapMode: VueTypes.string.def('OFF'),
        showHeatmapOption: VueTypes.bool.def(false),
        canSaveBpmn: VueTypes.bool.def(false),
        frequencyIndicatorState: VueTypes.shape({
            visible: VueTypes.bool,
            value: VueTypes.number,
            minValue: VueTypes.number,
            canIncrease: VueTypes.bool,
            canDecrease: VueTypes.bool,
        }).def({ visible: false, value: 100, minValue: 0, canIncrease: false, canDecrease: false }),
        disableElements: VueTypes.bool.def(false),
    },
    computed: {
        percentageModeModel: {
            get() { return this.percentageMode },
            set(value) { this.handleChangePercentage(value) },
        },
        directionValueModel: {
            get() { return this.directionValue },
            set(value) { this.handleChangeDirection(value) },
        },
        statsValueModel: {
            get() { return this.statsValue },
            set(value) { this.handleChangeStats(value) },
        },
        heatmapModeModel: {
            get() { return this.heatmapMode },
            set(value) { this.handleChangeHeatmap(value) },
        },
        percentageOptions() {
            return [
                { value: 'TOTAL', label: this.$t('visualization.percentageTotal') },
                { value: 'FILTERED', label: this.$t('visualization.percentageFiltered') },
            ]
        },
        directionOptions() {
            return [
                { value: 'LR', label: 'Horizontal' },
                { value: 'TB', label: 'Vertical' },
            ]
        },
        statsOptions() {
            return [
                { value: 'variances', label: this.$t('visualization.filters.contextualInfo.sd') },
                { value: 'averages', label: this.$t('visualization.filters.contextualInfo.avg') },
                { value: 'median', label: this.$t('visualization.filters.contextualInfo.med') },
                { value: 'min', label: this.$t('visualization.filters.contextualInfo.min') },
                { value: 'max', label: this.$t('visualization.filters.contextualInfo.max') },
            ]
        },
        heatmapOptions() {
            return [
                { value: 'OFF', label: 'Off' },
                { value: 'FREQUENCY', label: 'Frequency' },
                { value: 'DURATION', label: 'Duration' },
            ]
        },
    },
    methods: {
        handleChangePercentage(value) {
            this.$emit('on-change-percentage', value)
        },
        handleChangeDirection(value) {
            this.$emit('on-change-direction', value)
        },
        handleChangeStats(value) {
            this.$emit('on-change-stats', value)
        },
        handleChangeHeatmap(value) {
            this.$emit('on-change-heatmap', value)
        },
        handleSaveBpmn() {
            this.$emit('on-save-bpmn')
        },
        handleIncreaseFrequencyIndicator() {
            this.$emit('on-increase-frequency-indicator')
        },
        handleDecreaseFrequencyIndicator() {
            this.$emit('on-decrease-frequency-indicator')
        },
    },
}
</script>

<style lang="scss" scoped>
.ProcessDisplayDrawerPanel {
    width: 100%;
}

:deep(.ContextDrawerExpansion) {
    border-top: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansion.ContextDrawerExpansion--last) {
    border-bottom: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansionHeader .q-item__label) {
    font-weight: 700;
}

:deep(.ContextDrawerExpansionHeader .q-item__section--avatar .q-icon) {
    color: $primary;
}

.ContextDrawerExpansionContent {
    padding: 8px 16px 16px;
}

.HeatmapLegend {
    display: flex;
    align-items: center;
    gap: 8px;
}

.HeatmapLegendBar {
    flex: 1 1 auto;
    height: 8px;
    border-radius: 999px;
    border: 1px solid $gray-04;
    background: linear-gradient(90deg, #fff36f 0%, #f63737 100%);
}

.HeatmapLegendLabel {
    font-size: 12px;
    color: $gray-07;
}
</style>
